import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import Webcam from 'react-webcam';
import { Shield, Lock, FileCheck, AlertTriangle } from 'lucide-react';
import { API, BACKEND_URL } from '@/config/api';

// NOTE: API base is centralized in `src/config/api.js` (supports relative `/api` and avoids localhost leakage).

export default function Auth({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState('login'); // login, register, otp, face
  const [formData, setFormData] = useState({
    mobile: '',
    email: '',
    name: '',
    otp: ''
  });
  const [loading, setLoading] = useState(false);
  const [tempUserId, setTempUserId] = useState(null);
  const [requiresFace, setRequiresFace] = useState(false);
  const webcamRef = useRef(null);
  const cardRef = useRef(null);

  // If already authenticated and on the login page, redirect to dashboard
  useEffect(() => {
    const existingToken = localStorage.getItem('udt_token');
    if (existingToken && location.pathname === '/login') {
      navigate('/dashboard', { replace: true });
    }
  }, [location.pathname, navigate]);

  // 3D card tilt effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = (y - centerY) / 20;
        const rotateY = (centerX - x) / 20;
        
        cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(0)`;
      }
    };

    const handleMouseLeave = () => {
      if (cardRef.current) {
        cardRef.current.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateZ(0)';
      }
    };

    const card = cardRef.current;
    if (card) {
      card.addEventListener('mousemove', handleMouseMove);
      card.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (card) {
        card.removeEventListener('mousemove', handleMouseMove);
        card.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [mode]);

  // Reset card transform when mode changes
  useEffect(() => {
    if (cardRef.current) {
      cardRef.current.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateZ(0)';
    }
  }, [mode]);

  const handleRegister = async () => {
    if (!formData.mobile || !formData.email || !formData.name) {
      toast.error('Please fill all fields');
      return;
    }

    setLoading(true);
    try {
      // Normalize mobile number: remove + and spaces, ensure it starts with country code
      let normalizedMobile = formData.mobile.replace(/\s+/g, '').replace(/^\+/, '');
      
      // If it doesn't start with 91, add it (assuming Indian numbers)
      if (!normalizedMobile.startsWith('91')) {
        normalizedMobile = `91${normalizedMobile}`;
      }
      
      const response = await axios.post(`${API}/auth/register`, {
        mobile: normalizedMobile,
        email: formData.email,
        name: formData.name
      });
      toast.success(response.data.message);
      toast.info('Please check your phone for the OTP');
      // Store normalized mobile for OTP verification
      setFormData({ ...formData, mobile: normalizedMobile });
      setTempUserId(response.data.user_id);
      setMode('otp');
    } catch (error) {
      console.error('❌ Registration Error:', error);
      console.error('Backend Response:', error.response?.data);
      console.error('Status:', error.response?.status);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!formData.mobile) {
      toast.error('Please enter mobile number');
      return;
    }

    setLoading(true);
    try {
      // Normalize mobile number: remove + and spaces, ensure it starts with country code
      let normalizedMobile = formData.mobile.replace(/\s+/g, '').replace(/^\+/, '');
      
      // If it doesn't start with 91, add it (assuming Indian numbers)
      if (!normalizedMobile.startsWith('91')) {
        normalizedMobile = `91${normalizedMobile}`;
      }
      
      const response = await axios.post(`${API}/auth/login?mobile=${normalizedMobile}`);
      toast.success(response.data.message);
      toast.info('Please check your phone for the OTP');
      // Store normalized mobile for OTP verification
      setFormData({ ...formData, mobile: normalizedMobile });
      setMode('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!formData.otp) {
      toast.error('Please enter OTP');
      return;
    }

    setLoading(true);
    try {
      // Normalize mobile number for verification (same as login/register)
      let normalizedMobile = formData.mobile.replace(/\s+/g, '').replace(/^\+/, '');
      if (!normalizedMobile.startsWith('91')) {
        normalizedMobile = `91${normalizedMobile}`;
      }
      
      const response = await axios.post(`${API}/auth/verify-otp`, {
        mobile: normalizedMobile,
        otp: formData.otp
      });
      
      // Face verification is optional - directly login
      onLogin(response.data.user, response.data.token);
      toast.success('Login successful');

      const redirectTo = location.state?.from || '/dashboard';
      navigate(redirectTo, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFaceVerification = async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      toast.error('Unable to capture image');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-face`, {
        user_id: tempUserId,
        face_image: imageSrc
      });
      
      if (response.data.verified) {
        // Re-verify OTP to get token (use normalized mobile)
        let normalizedMobile = formData.mobile.replace(/\s+/g, '').replace(/^\+/, '');
        if (!normalizedMobile.startsWith('91')) {
          normalizedMobile = `91${normalizedMobile}`;
        }
        const otpResponse = await axios.post(`${API}/auth/verify-otp`, {
          mobile: normalizedMobile,
          otp: formData.otp
        });
        onLogin(otpResponse.data.user, otpResponse.data.token);
        toast.success('Face verified successfully');

        const redirectTo = location.state?.from || '/dashboard';
        navigate(redirectTo, { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Face verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-[#E8F2FF] via-[#D4E8FF] to-[#F5F9FF] bg-[length:400%_400%] animate-gradient-shift">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-[300px] h-[300px] -top-[100px] -left-[100px] rounded-full bg-gradient-to-br from-[rgba(15,82,186,0.1)] to-[rgba(15,82,186,0.05)] backdrop-blur-[40px] animate-float"></div>
        <div className="absolute w-[200px] h-[200px] top-1/2 -right-[50px] rounded-full bg-gradient-to-br from-[rgba(15,82,186,0.1)] to-[rgba(15,82,186,0.05)] backdrop-blur-[40px] animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute w-[250px] h-[250px] -bottom-[100px] left-[20%] rounded-full bg-gradient-to-br from-[rgba(15,82,186,0.1)] to-[rgba(15,82,186,0.05)] backdrop-blur-[40px] animate-float" style={{ animationDelay: '4s' }}></div>
        <div className="absolute w-[150px] h-[150px] top-[20%] right-[10%] rounded-full bg-gradient-to-br from-[rgba(15,82,186,0.1)] to-[rgba(15,82,186,0.05)] backdrop-blur-[40px] animate-float" style={{ animationDelay: '6s' }}></div>
      </div>

      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-6 md:gap-8 items-center relative z-10 px-0 sm:px-2 min-w-0">
        {/* Left side - Branding */}
        <div className="space-y-4 sm:space-y-6 animate-fade-in-up-slide min-w-0">
          {/* Supporting icon + title (logo image temporarily removed) */}
          <div className="flex items-center gap-3 animate-icon-bounce">
            <div className="[perspective:1000px] inline-block group flex-shrink-0">
              <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-[#0F52BA] drop-shadow-[0_4px_8px_rgba(15,82,186,0.3)] animate-shield-rotate transition-all duration-300 group-hover:scale-110 group-hover:drop-shadow-[0_6px_12px_rgba(15,82,186,0.5)]" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-[#0F52BA] to-[#1E7CE8] animate-logo-pulse [text-shadow:0_0_30px_rgba(15,82,186,0.3)]" style={{ fontFamily: 'Work Sans' }}>UDT</h1>
          </div>
          <h2 className="text-xl sm:text-3xl font-semibold text-[#1a1a1a] animate-title-slide" style={{ fontFamily: 'Work Sans' }}>Universal Document Transfer</h2>
          <p className="text-sm sm:text-lg text-[#666] animate-description-fade">Secure document sharing with AI-powered misuse detection and watermark chain tracking</p>
          
          <div className="space-y-4 mt-8">
            <div className="flex items-start gap-3 animate-feature-slide transition-all duration-300 hover:translate-x-2.5 hover:pl-1.5 group">
              <div className="[perspective:500px] inline-block">
                <Lock className="w-5 h-5 mt-1 text-[#0F52BA] drop-shadow-[0_2px_4px_rgba(15,82,186,0.2)] transition-all duration-300 group-hover:scale-125 group-hover:rotate-[5deg] group-hover:drop-shadow-[0_4px_8px_rgba(15,82,186,0.4)]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1a1a1a]">End-to-End Encryption</h3>
                <p className="text-sm text-[#666]">AES-256 encryption for all documents</p>
              </div>
            </div>
            <div className="flex items-start gap-3 animate-feature-slide transition-all duration-300 hover:translate-x-2.5 hover:pl-1.5 group" style={{ animationDelay: '0.1s' }}>
              <div className="[perspective:500px] inline-block">
                <FileCheck className="w-5 h-5 mt-1 text-[#0F52BA] drop-shadow-[0_2px_4px_rgba(15,82,186,0.2)] transition-all duration-300 group-hover:scale-125 group-hover:rotate-[5deg] group-hover:drop-shadow-[0_4px_8px_rgba(15,82,186,0.4)]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1a1a1a]">Watermark Chain Tracking</h3>
                <p className="text-sm text-[#666]">Track every document share with unique watermarks</p>
              </div>
            </div>
            <div className="flex items-start gap-3 animate-feature-slide transition-all duration-300 hover:translate-x-2.5 hover:pl-1.5 group" style={{ animationDelay: '0.2s' }}>
              <div className="[perspective:500px] inline-block">
                <AlertTriangle className="w-5 h-5 mt-1 text-[#0F52BA] drop-shadow-[0_2px_4px_rgba(15,82,186,0.2)] transition-all duration-300 group-hover:scale-125 group-hover:rotate-[5deg] group-hover:drop-shadow-[0_4px_8px_rgba(15,82,186,0.4)]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1a1a1a]">AI Misuse Detection</h3>
                <p className="text-sm text-[#666]">Real-time monitoring with automatic document lock</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Auth Form */}
        <Card 
          ref={cardRef}
          className="w-full min-w-0 max-w-full [transform-style:preserve-3d] [perspective:1000px] transition-[transform,box-shadow] duration-100 ease-out shadow-[0_20px_60px_rgba(15,82,186,0.2),0_0_0_1px_rgba(15,82,186,0.05)] backdrop-blur-[10px] bg-[rgba(255,255,255,0.95)] hover:shadow-[0_30px_80px_rgba(15,82,186,0.3),0_0_0_1px_rgba(15,82,186,0.1)]" 
          data-testid="auth-card"
        >
          <CardHeader className="animate-slide-in-right">
            <CardTitle data-testid="auth-title" className="animate-title-glow">
              {mode === 'login' && 'Sign In'}
              {mode === 'register' && 'Create Account'}
              {mode === 'otp' && 'Verify OTP'}
              {mode === 'face' && 'Face Verification'}
            </CardTitle>
            <CardDescription className="animate-description-fade">
              {mode === 'login' && 'Enter your mobile number to continue'}
              {mode === 'register' && 'Create your secure UDT account'}
              {mode === 'otp' && 'Enter the OTP sent to your mobile'}
              {mode === 'face' && 'Position your face in the camera'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 animate-content-fade">
            {(mode === 'login' || mode === 'register' || mode === 'otp') && (
              <div className="space-y-4">
                {(mode === 'register' || mode === 'login' || mode === 'otp') && (
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile Number</Label>
                    <Input
                      id="mobile"
                      data-testid="mobile-input"
                      type="tel"
                      placeholder="+91 9876543210 or 9876543210"
                      value={formData.mobile}
                      onChange={(e) => {
                        // Allow +, numbers, and spaces for better UX
                        const value = e.target.value;
                        setFormData({ ...formData, mobile: value });
                      }}
                      disabled={mode === 'otp'}
                      className="transition-all duration-300 [transform-style:preserve-3d] focus:translate-z-[5px] focus:shadow-[0_0_0_3px_rgba(15,82,186,0.1),0_4px_12px_rgba(15,82,186,0.15)] focus:border-[#0F52BA]"
                    />
                  </div>
                )}
                
                {mode === 'register' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        data-testid="email-input"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="transition-all duration-300 [transform-style:preserve-3d] focus:translate-z-[5px] focus:shadow-[0_0_0_3px_rgba(15,82,186,0.1),0_4px_12px_rgba(15,82,186,0.15)] focus:border-[#0F52BA]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        data-testid="name-input"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="transition-all duration-300 [transform-style:preserve-3d] focus:translate-z-[5px] focus:shadow-[0_0_0_3px_rgba(15,82,186,0.1),0_4px_12px_rgba(15,82,186,0.15)] focus:border-[#0F52BA]"
                      />
                    </div>
                  </>
                )}

                {mode === 'otp' && (
                  <div className="space-y-2">
                    <Label htmlFor="otp">OTP</Label>
                    <Input
                      id="otp"
                      data-testid="otp-input"
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={formData.otp}
                      onChange={(e) => setFormData({ ...formData, otp: e.target.value.replace(/\D/g, '') })}
                      maxLength={6}
                      className="transition-all duration-300 [transform-style:preserve-3d] focus:translate-z-[5px] focus:shadow-[0_0_0_3px_rgba(15,82,186,0.1),0_4px_12px_rgba(15,82,186,0.15)] focus:border-[#0F52BA]"
                    />
                  </div>
                )}
              </div>
            )}

            {mode === 'face' && (
              <div className="space-y-4">
                <div className="relative rounded-lg overflow-hidden" style={{ border: '3px solid #0F52BA' }}>
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    className="w-full"
                    videoConstraints={{ facingMode: 'user' }}
                  />
                </div>
                <p className="text-sm text-center" style={{ color: '#666' }}>Look directly at the camera for verification</p>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-3 animate-mode-transition">
            {mode === 'login' && (
              <>
                <Button
                  data-testid="login-button"
                  onClick={handleLogin}
                  disabled={loading}
                  className="w-full relative [transform-style:preserve-3d] transition-all duration-300 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] shadow-[0_4px_15px_rgba(15,82,186,0.3),0_0_0_0_rgba(15,82,186,0.2)] overflow-hidden hover:-translate-y-1 hover:scale-[1.02] hover:shadow-[0_8px_25px_rgba(15,82,186,0.4),0_0_0_4px_rgba(15,82,186,0.1)] active:-translate-y-0.5 active:scale-[0.98] active:shadow-[0_2px_10px_rgba(15,82,186,0.3)] disabled:transform-none disabled:opacity-60 disabled:cursor-not-allowed before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-full"
                  style={{ background: '#0F52BA' }}
                >
                  {loading ? 'Processing...' : 'Send OTP'}
                </Button>
                <Button
                  data-testid="switch-to-register"
                  variant="ghost"
                  onClick={() => setMode('register')}
                  className="w-full"
                >
                  New user? Create account
                </Button>
              </>
            )}

            {mode === 'register' && (
              <>
                <Button
                  data-testid="register-button"
                  onClick={handleRegister}
                  disabled={loading}
                  className="w-full relative [transform-style:preserve-3d] transition-all duration-300 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] shadow-[0_4px_15px_rgba(15,82,186,0.3),0_0_0_0_rgba(15,82,186,0.2)] overflow-hidden hover:-translate-y-1 hover:scale-[1.02] hover:shadow-[0_8px_25px_rgba(15,82,186,0.4),0_0_0_4px_rgba(15,82,186,0.1)] active:-translate-y-0.5 active:scale-[0.98] active:shadow-[0_2px_10px_rgba(15,82,186,0.3)] disabled:transform-none disabled:opacity-60 disabled:cursor-not-allowed before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-full"
                  style={{ background: '#0F52BA' }}
                >
                  {loading ? 'Creating...' : 'Create Account'}
                </Button>
                <Button
                  data-testid="switch-to-login"
                  variant="ghost"
                  onClick={() => setMode('login')}
                  className="w-full"
                >
                  Already have an account? Sign in
                </Button>
              </>
            )}

            {mode === 'otp' && (
              <>
                <Button
                  data-testid="verify-otp-button"
                  onClick={handleVerifyOTP}
                  disabled={loading}
                  className="w-full relative [transform-style:preserve-3d] transition-all duration-300 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] shadow-[0_4px_15px_rgba(15,82,186,0.3),0_0_0_0_rgba(15,82,186,0.2)] overflow-hidden hover:-translate-y-1 hover:scale-[1.02] hover:shadow-[0_8px_25px_rgba(15,82,186,0.4),0_0_0_4px_rgba(15,82,186,0.1)] active:-translate-y-0.5 active:scale-[0.98] active:shadow-[0_2px_10px_rgba(15,82,186,0.3)] disabled:transform-none disabled:opacity-60 disabled:cursor-not-allowed before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-full"
                  style={{ background: '#0F52BA' }}
                >
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setMode('login')}
                  className="w-full"
                >
                  Back to login
                </Button>
              </>
            )}

            {mode === 'face' && (
              <>
                <Button
                  data-testid="verify-face-button"
                  onClick={handleFaceVerification}
                  disabled={loading}
                  className="w-full relative [transform-style:preserve-3d] transition-all duration-300 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] shadow-[0_4px_15px_rgba(15,82,186,0.3),0_0_0_0_rgba(15,82,186,0.2)] overflow-hidden hover:-translate-y-1 hover:scale-[1.02] hover:shadow-[0_8px_25px_rgba(15,82,186,0.4),0_0_0_4px_rgba(15,82,186,0.1)] active:-translate-y-0.5 active:scale-[0.98] active:shadow-[0_2px_10px_rgba(15,82,186,0.3)] disabled:transform-none disabled:opacity-60 disabled:cursor-not-allowed before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-full"
                  style={{ background: '#0F52BA' }}
                >
                  {loading ? 'Verifying...' : 'Verify Face'}
                </Button>
              </>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}

